import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { fetchPresence } from '../model/presence';
import {
  selectPresenceStatus,
  selectWatchedUserIds,
  usePresenceStore,
  type PresenceStatus,
} from '../model/presenceStore';

/**
 * Раз в столько статус переспрашивается.
 *
 * <p>Здесь раньше стояло объяснение, почему мы не делаем push по сокету: «завели бы подписку на
 * произвольные userId, то есть готовый способ следить за человеком с секундной точностью».
 * Обоснование было ложным — следить и так было можно, просто поллингом: запрос был анонимным, по
 * двести человек за раз, без действующего лимита по IP. Дыру закрыли гейтом на шлюзе
 * (`@Current` + фильтр блокировок), и приватность больше не аргумент против push.
 *
 * <p>Настоящая причина проще: **push нам не нужен по масштабу**. При 150–200 одновременных
 * пользователях поллинг раз в тридцать секунд — это около семи запросов в секунду, причём самых
 * дешёвых в системе; на фоне тяжёлых запросов шлюза его не видно. Продуктовой нужды тоже нет: сам
 * AWAY включается через пять минут бездействия, так что секундная свежесть точке ни к чему.
 *
 * <p>Пересматривать, когда: доля presence в запросах шлюза перевалит за 20%, одновременных
 * пользователей станет за две тысячи, или продукту понадобится живость уровня «печатает…».
 * Тогда это подписка на дельты по топикам (`BroadcastSubscriptionService` уже их умеет), а не
 * «спроси-ответь» по сокету: последнее оставляет поллинг на месте и лишает нас кеша и
 * дедупликации react-query.
 */
const PRESENCE_REFRESH_MS = 30_000;

/**
 * Возвращает статус одного человека и на время жизни компонента добавляет его в общий опрос.
 * Пока сервер не ответил — {@code undefined}: это «не знаю», а не «офлайн», и рисовать по нему
 * ничего нельзя.
 */
export const usePresenceStatus = (userId?: string | null): PresenceStatus | undefined => {
  const watch = usePresenceStore((state) => state.watch);
  const unwatch = usePresenceStore((state) => state.unwatch);
  const status = usePresenceStore(selectPresenceStatus(userId));

  useEffect(() => {
    if (!userId) {
      return;
    }

    watch(userId);
    return () => unwatch(userId);
  }, [userId, watch, unwatch]);

  return status;
};

/**
 * Единственное место, которое ходит на сервер за статусами: собирает всех, на кого сейчас смотрят
 * экраны, и спрашивает про них одним запросом. Раньше статус ехал полем внутри профиля вместе со
 * списком чатов и с открытым чатом — это давало два снимка разного возраста, из-за чего в списке
 * человек был офлайн, а в самом чате онлайн.
 *
 * Монтируется один раз в корневом лейауте.
 */
export const usePresenceSync = () => {
  const isAuth = useIsAuth();
  const watchers = usePresenceStore(selectWatchedUserIds);
  const applyStatuses = usePresenceStore((state) => state.applyStatuses);

  // Ключ должен зависеть от состава, а не от порядка появления — иначе запрос перезапускался бы
  // каждый раз, когда список тех же самых людей собрался в другом порядке.
  const userIds = useMemo(() => Object.keys(watchers).toSorted(), [watchers]);

  const { data } = useQuery({
    queryKey: ['presence', userIds],
    queryFn: () => fetchPresence(userIds),
    enabled: isAuth && userIds.length > 0,
    refetchInterval: PRESENCE_REFRESH_MS,
    refetchOnWindowFocus: true,
    // Ошибку не показываем и прошлые статусы не сбрасываем: при рестарте ws или сетевом флапе
    // индикаторы должны остаться прежними, а не погаснуть у всех разом.
    retry: false,
    staleTime: PRESENCE_REFRESH_MS,
  });

  useEffect(() => {
    if (data) {
      applyStatuses(data);
    }
  }, [data, applyStatuses]);
};
